import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChatMock from '../components/chat/ChatMock.jsx';
import './AssistantSection.css';

// How long each thing you can ask for stays before the next one rolls in
const ASK_DURATION_MS = 2500;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * True when the visitor has asked their system to reduce motion.
 * @returns {boolean} Whether to avoid animation
 */
function usePrefersReducedMotion() {
  // False until after mount so the server HTML matches the first client render.
  const [prefersReduced, setPrefersReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const handleChange = () => setPrefersReduced(query.matches);
    handleChange();
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  return prefersReduced;
}

/**
 * CSS state class for a rolling item: shown, on its way out, or waiting below.
 * @param {boolean} isActive - Currently shown
 * @param {boolean} isLeaving - Rolling out upwards
 * @returns {string} Class suffix
 */
function rollState(isActive, isLeaving) {
  if (isActive) return ' is-active';
  if (isLeaving) return ' is-leaving';
  return '';
}

/**
 * What Revetall is, right under the top: the assistant sentence, then one row «Be om [pill]
 * Du får det med en gang.» with what you can ask for rolling in the pill (`assistant.asks`).
 * Next to it, a chat asking about all the companies at once (`assistant.demo`).
 * With reduced motion the first item is shown, standing still.
 * @returns {JSX.Element} Assistant section
 */
function AssistantSection() {
  const { t } = useTranslation();
  const asks = t('assistant.asks', { returnObjects: true });
  const prefersReducedMotion = usePrefersReducedMotion();
  const [askIndex, setAskIndex] = useState(0);
  const [previousAskIndex, setPreviousAskIndex] = useState(null);

  useEffect(() => {
    if (prefersReducedMotion) return undefined;

    const timer = setTimeout(() => {
      setPreviousAskIndex(askIndex);
      setAskIndex((askIndex + 1) % asks.length);
    }, ASK_DURATION_MS);

    return () => clearTimeout(timer);
  }, [askIndex, prefersReducedMotion, asks.length]);

  const shownIndex = prefersReducedMotion ? 0 : askIndex;
  const leavingIndex = prefersReducedMotion ? null : previousAskIndex;

  return (
    <section className="assistant">
      <div className="assistant-content">
        <div>
          <h2 className="assistant-text">{t('assistant.text')}</h2>

          <p className="assistant-ask">
            <span>{t('assistant.askLeadIn')}</span>
            {/* Screen readers and search engines get every item once */}
            <span className="visually-hidden">{asks.join(' ')}</span>
            <span className="assistant-ask-rotator" aria-hidden="true">
              {asks.map((ask, index) => (
                <span
                  key={ask}
                  className={`assistant-ask-item${rollState(
                    index === shownIndex,
                    index === leavingIndex
                  )}`}
                >
                  {ask}
                </span>
              ))}
            </span>
            <span>{t('assistant.result')}</span>
          </p>
        </div>

        <div className="assistant-art">
          <ChatMock chat={t('assistant.demo', { returnObjects: true })} />
        </div>
      </div>
    </section>
  );
}

export default AssistantSection;
